/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import classNames from "classnames";

// @material-ui/core components
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@material-ui/core";

// @material-ui/icons
import { Lock, Mail } from "@material-ui/icons";

// core components
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";

import {
  sLogin,
  sProceed,
  sForgotPW,
  sLoginFail,
  sLoginFailMsg,
  sConfirmEmailMsg,
  sResetPassword,
  sVerifyReqFail,
  sVerifyReqFailMsg,
  sConfirmEmail,
  sVerifyMailSent,
  sRetry,
  sOrByMail,
  sBySocial,
  sSignUpUser,
  sNoAccount,
} from "assets/jss/views/ResStrings";
import { showHtmlText } from "client/views/Exercise/ExMain";
import { idSubmit, idForgetPW, fnLoginForm } from "assets/jss/views/ElementIDs";
import { progressCircles } from "components/Form/FormField";

import {
  authenticateUser,
  resendCode,
} from "client/views/Account/authCognito.jsx";
import { checkAccessCode } from "./accountUtils";
import { ResetDialog } from "client/views/Account/ResetDialog";

import {
  loginValidation,
  loginSocialButtons,
  FormTextField,
  btnFormSubmit,
} from "components/Form/FormField";
import { Modal } from "components/Modal/Modal";
import { classicLogin, ApricotAction } from "client/actions/user.ts";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface Props extends WithStyles<typeof styles> {
  classicLogin: ApricotAction;
  onExit: () => void;
  onSignUp: () => void;
}

const LoginDialogBase: React.FC<Props> = ({
  // actions
  classicLogin,
  // component props
  onExit,
  onSignUp,
  classes,
}: Props) => {
  const [showReset, setShowReset] = useState(false);
  const [presetEmail, setPresetEmail] = useState<string | undefined>(undefined);
  const [loginStart, setLoginStart] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  const ref = React.createRef();

  const showMessage = (
    title: string,
    body: string,
    warning: boolean = false,
    buttons: string[] = [],
    onButtons: (id: number, text?: string | null) => void = () => {},
  ) => {
    setAlertModal(
      <Modal
        title={title}
        buttons={buttons}
        color={warning ? "warning" : "secondary"}
        onButtons={onButtons}
        onExit={() => setShowAlert(false)}>
        {showHtmlText(body)}
      </Modal>,
    );
    setShowAlert(true);
  };

  const loginUser = async (email: string, password: string) => {
    setLoginStart(true);
    var res = await authenticateUser(email, password);

    if (res.result) {
      // login success, forward to Passport mechanism
      classicLogin(email);
    } else {
      setLoginStart(false);

      if (res.err.code === "UserNotConfirmedException") {
        // user signed up, but didn't confirm email
        confirmUser(email);
      } else {
        // general error, with a button to allow Reset Password
        showMessage(
          sLoginFail,
          sLoginFailMsg,
          true,
          [sRetry, sResetPassword],
          (id: number) => {
            if (id === 1) {
              setShowReset(true);
            }
            // else: try again, still in Login Dialog
          },
        );
      }
    }
  };

  const confirmUser = async (email: string) => {
    // ask if user wants to request a code for email confirmation
    showMessage(
      sConfirmEmail,
      sConfirmEmailMsg,
      false,
      [sProceed],
      async () => {
        var res = await resendCode(email);
        if (res.result) {
          // resend requested, let user know an email is sent
          showMessage(sConfirmEmail, sVerifyMailSent);
        } else {
          // resend request just fails, have user try again later
          showMessage(sVerifyReqFail, sVerifyReqFailMsg, true);
        }
      },
    );
  };

  const renderLoginInput = () => (
    <>
      <Field
        name="email"
        type="text"
        icon={<Mail />}
        component={FormTextField}
        label="Email"
      />
      <Field
        name="password"
        type="password"
        icon={<Lock />}
        component={FormTextField}
        label="Password"
      />
    </>
  );

  const closeDlg = () => onExit();

  const chkCode = checkAccessCode(closeDlg);
  if (chkCode.passed === false) {
    return chkCode.alertModal;
  }

  return (
    <>
      {loginStart && progressCircles()}
      {showAlert && alertModal}
      {showReset && (
        <ResetDialog
          onExit={(email?: string) => {
            setShowReset(false);
            setPresetEmail(email);
          }}
        />
      )}
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal + " " + classes.modalLogin,
        }}
        open={true}
        ref={ref}
        onClose={closeDlg}>
        <Card plain className={classes.modalLoginCard}>
          <DialogTitle disableTypography className={classes.modalHeader}>
            <CardHeader plain signup>
              <h4
                style={{ fontWeight: 700 }}
                className={classes.cardTitleWhite}>
                {sLogin}
              </h4>
              <h5
                style={{ fontWeight: 300 }}
                className={classes.cardTitleWhite}>
                {sBySocial}
              </h5>
              <div className={classes.socialLine}>
                {loginSocialButtons(classes)}
              </div>
            </CardHeader>
          </DialogTitle>
          <DialogContent className={classes.modalBody}>
            <h5 style={{ textAlign: "center", marginBottom: "0" }}>
              {sOrByMail}
            </h5>
            <CardBody className={classes.cardLoginBody}>
              <Formik
                enableReinitialize
                initialValues={{
                  email: presetEmail || "",
                  password: "",
                }}
                onSubmit={(values) => {
                  loginUser(values.email, values.password);
                }}
                validationSchema={loginValidation}
                render={({ isValid }) => (
                  <Form name={fnLoginForm}>
                    {renderLoginInput()}
                    <DialogActions
                      className={classNames(
                        classes.modalFooter,
                        classes.justifyContentCenter,
                      )}>
                      {btnFormSubmit({
                        label: sLogin,
                        disabled: !isValid || loginStart,
                        id: idSubmit,
                      })}
                    </DialogActions>
                  </Form>
                )}
              />
              <Divider variant="fullWidth" />
              <div
                style={{
                  textAlign: "center",
                  margin: "0.5rem auto 1rem auto",
                }}>
                <p style={{ display: "inline-block" }}>
                  {sForgotPW}
                  <span
                    id={idForgetPW}
                    className={classes.resetPW}
                    onClick={() => setShowReset(true)}>
                    &nbsp;&nbsp;{sResetPassword}
                  </span>
                </p>
                <p style={{ display: "inline-block" }}>
                  {sNoAccount}
                  <span
                    className={classes.signupLink}
                    onClick={() => {
                      closeDlg();
                      onSignUp();
                    }}>
                    &nbsp;&nbsp;{sSignUpUser}
                  </span>
                </p>
              </div>
            </CardBody>
          </DialogContent>
        </Card>
      </Dialog>
    </>
  );
};

const base: any = LoginDialogBase;
export const LoginDialog: any = withStyles(styles)(
  connect(null, { classicLogin })(base),
);

export const LoginDialogTest = withStyles(styles)(base);
